import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { IOption } from '@user/components/Filters'
import { LeadType } from '@user/components/Leads/LeadFilters/typings'
import { AddRequiredFieldDialog } from '@user/components/Settings/PendingLeadSettings/AddRequiredFieldDialog'
import { FormSettingsLink } from '@user/components/Settings/PendingLeadSettings/FormSettingsLink'
import { RequiredFieldRow } from '@user/components/Settings/PendingLeadSettings/RequiredFieldRow'
import { SYSTEM_REQUIRED_FIELDS } from '@user/components/Settings/PendingLeadSettings/constants'
import { getFieldKey } from '@user/components/Settings/PendingLeadSettings/helpers'
import { useData } from '@user/components/Settings/PendingLeadSettings/hooks/requiredFieldsSection/useData'
import { useHandlers } from '@user/components/Settings/PendingLeadSettings/hooks/requiredFieldsSection/useHandlers'
import type { IAutoReviewAdditionalField } from '@user/store/autoReviewSettings'

type Props = {
  leadTypes: IOption[]
  visibleSelectedTypes: LeadType[]
  requiredAdditionalFields: IAutoReviewAdditionalField[]
  fieldOptions: IAutoReviewAdditionalField[]
  handleAddFields: (selection: IAutoReviewAdditionalField[]) => void
  handleRemoveField: (field: IAutoReviewAdditionalField) => void
  handleManageFormSettings: () => void
}

export const RequiredFieldsSection = (props: Props) => {
  const {
    requiredAdditionalFields,
    fieldOptions,
    handleAddFields,
    handleRemoveField,
    handleManageFormSettings,
  } = props

  const data = useData(props)
  const {
    styles,
    dialogOpen,
    leadTypeLabels,
    hasAdditionalFields,
    addFieldDisabled,
  } = data
  const { openDialog, closeDialog, handleAdd, handleRemove } = useHandlers(
    data,
    { handleAddFields, handleRemoveField },
  )

  return (
    <Paper variant="outlined" sx={styles.paper}>
      <Stack
        display="flex"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb="6px"
      >
        <Typography variant="subtitle3">Required Fields</Typography>
        <Button
          variant="contained"
          disabled={addFieldDisabled}
          onClick={openDialog}
          data-test-id="pending-lead-add-field-button"
        >
          Add Field
        </Button>
      </Stack>

      <Alert
        sx={{
          display: 'flex',
          alignItems: 'start',
          mb: '14px',
        }}
        severity="info"
      >
        <Typography variant="body2">
          Add the fields a pending lead must have before it can be
          auto-reviewed. These are separate from your existing{' '}
          <FormSettingsLink
            label="Lead & Opportunity Form Settings"
            onClick={handleManageFormSettings}
          />
          , which govern company-required fields checked later at opportunity
          conversion.
        </Typography>
      </Alert>

      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        justifyContent={'space-between'}
      >
        <Typography variant="subtitle3">Required by System</Typography>
        <FormSettingsLink
          label="Lead and Opportunity Form Settings"
          onClick={handleManageFormSettings}
        />
      </Stack>

      {SYSTEM_REQUIRED_FIELDS.map(
        ({ label, determinedByAssignment }, index) => (
          <RequiredFieldRow
            key={label}
            label={label}
            secondaryText={
              determinedByAssignment
                ? 'Determined by assignment rules'
                : undefined
            }
            testId={`pending-lead-system-field-${index}`}
          />
        ),
      )}

      {hasAdditionalFields && (
        <Box mt={2}>
          <Typography variant="subtitle3">Additional Fields</Typography>
          {requiredAdditionalFields.map((field) => (
            <RequiredFieldRow
              key={getFieldKey(field)}
              label={field.fieldName}
              onRemove={handleRemove(field)}
              testId={`pending-lead-additional-field-${getFieldKey(field)}`}
            />
          ))}
        </Box>
      )}

      <AddRequiredFieldDialog
        open={dialogOpen}
        leadTypeLabels={leadTypeLabels}
        options={fieldOptions}
        added={requiredAdditionalFields}
        onCancel={closeDialog}
        onAdd={handleAdd}
      />
    </Paper>
  )
}

RequiredFieldsSection.displayName = 'RequiredFieldsSection'
